// src/services/socketService.ts
import io, { Socket } from 'socket.io-client';
import { ENV } from '../config/runtimeEnv';
import { subscribeAuthSession } from '../lib/http/session';
import { CLOSE_REAUTHENTICATE, CLOSE_SERVICE_RESTART, closeCodeOf, confirmSessionBeforeReconnect, reauthenticateAfterClose } from '../lib/realtime/reauth';

// Same-origin through the clinic gateway (contract §19): never io('/talk'), which Socket.IO would read as a namespace.
const TALK_SOCKET_PATH = '/talk/socket.io';

// Maximum buffered audio segments — oldest dropped on overflow to prevent memory exhaustion
const MAX_PENDING_AUDIO = 100;

interface PendingAudio {
	roomId: string;
	participantId: string;
	roomType?: string;
	timestamp: number;
	audioStartTimestamp?: number;
	sampleRate?: number;
	audioId?: string;
	data: ArrayBuffer;
}

class SocketService {
	private static instance: SocketService;
	private socket: typeof Socket | any = null;
	private reconnectAttempts = 0;
	private maxReconnectAttempts = 10;
	private baseReconnectDelay = 1000;
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private recoveringSession = false;
	private roomId: string = '';
	private roomType: string = '';
	// Room-bound capabilities, mutually exclusive: a paired phone's mic token wins over a patient's link; a practitioner sends neither (the session travels as a cookie).
	private consultationLink: string | null = null;
	private micToken: string | null = null;
	// Proof of admission beside the link, not a fourth credential; memory only, never logged.
	private admissionToken: string | null = null;
	private pendingAudioData: PendingAudio[] = [];

	// Dedup is BY ID ONLY. There was a second guard here keyed on an `audioHash` with a 5s window,
	// and nothing ever set one: `audioStreamingService.processFinalRecording()` is the only producer
	// of a segment and it stamps a fresh random `audioId` per emit and no hash at all, so the branch
	// could not run. It was removed rather than wired — every duplicate this transport can actually
	// see (a segment offered twice, a reconnect replay) carries the SAME id and the id guard already
	// answers it, whereas hashing every WAV on the capture path would cost CPU on the audio hot path
	// to catch only a case that must be fixed at its cause (two AudioStreamingService instances on
	// one room), not masked here.
	private sentAudioIds = new Set<string>();

	private isInitialized = false;
	private ongoingTransmissions = new Set<string>();

	private static watchingSession = false;

	private constructor() {
		console.log("SocketService: Creating singleton instance");
		if (!SocketService.watchingSession) {
			SocketService.watchingSession = true;
			// A signed-out tab must not keep a socket the session admitted; capability sockets stay.
			subscribeAuthSession((next, previous) => {
				if (next.status === 'anonymous' && previous.status === 'authenticated') SocketService.instance?.closeSessionSocket();
			});
		}
	}

	private closeSessionSocket() {
		if (this.socket && this.reliesOnSession()) this.disconnect();
	}

	public static getInstance(): SocketService {
		if (!SocketService.instance) {
			SocketService.instance = new SocketService();
		}
		return SocketService.instance;
	}

	public initialize(
		roomId: string,
		roomType: string = 'online',
		options?: { consultationLink?: string; micToken?: string }
	): typeof Socket {
		const micToken = options?.micToken || null;
		const consultationLink = micToken ? null : (options?.consultationLink || null);

		// The credential is part of the identity of a connection, so a change of ANY of them has to
		// reconnect even for the same room: a practitioner opening the mic page on their own tablet
		// would otherwise keep the practitioner socket they arrived with, and the mic token would never
		// be presented at all.
		// ⚠️ The link needs its own clause, and the reasoning that said otherwise — "it IS the room id
		// the patient page passes, so a change of link is already a change of room" — holds for
		// link→link and NOT for cookie→link at the same room id. That is one navigation inside a single
		// SPA session: `VideoConsultation`'s `roomId` is the last path segment of the consultation link,
		// so opening `/patient/consultation/{that same segment}` afterwards matches on `roomId` and
		// `micToken` alike, returns early, and never assigns `consultationLink` — leaving the cookie in
		// place and the socket bound by the JWT, which is precisely the state S37 exists to prevent.
		if (
			this.isInitialized &&
			this.socket &&
			this.socket.connected &&
			this.roomId === roomId &&
			this.micToken === micToken &&
			this.consultationLink === consultationLink
		) {
			console.log('SocketService: Already initialized for this room');
			return this.socket;
		}

		if (this.roomId !== roomId || this.micToken !== micToken || this.consultationLink !== consultationLink) {
			this.admissionToken = null;
		}
		this.roomId = roomId;
		this.roomType = roomType;
		this.micToken = micToken;
		this.consultationLink = consultationLink;
		this.isInitialized = true;

		if (this.socket) {
			console.log('SocketService: Disconnecting existing socket before initializing for a new room');
			this.socket.disconnect();
			this.socket = null;
		}

		return this.connect();
	}

	public getSocket(): typeof Socket | null {
		return this.socket;
	}

	public connect(): typeof Socket {
		if (this.socket && this.socket.connected) {
			console.log('SocketService: Reusing existing connected socket');
			return this.socket;
		}

		const path = ENV.VITE_TALK_PATH || TALK_SOCKET_PATH;
		console.log('SocketService: Connecting to talk at', path);

		this.socket = io(window.location.origin, {
			path,
			// The gateway reads the session from the access cookie; Socket.IO's polling XHRs need this to send it.
			withCredentials: true,
			reconnection: true,
			reconnectionAttempts: 5,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
			timeout: 20000,
			forceNew: false,
			multiplex: true,
			// A callback, so every reconnect presents the admission token set after this socket was built.
			auth: (cb: (data: Record<string, string>) => void) => cb(this.buildAuth())
		});

		this.setupEventHandlers();
		return this.socket;
	}

	public setAdmissionToken(token: string | null) {
		this.admissionToken = token || null;
	}

	// Never a token (contract §19): the browser session reaches talk as a cookie through the gateway; only room-bound capabilities ride the handshake.
	private buildAuth(): Record<string, string> {
		const authPayload: Record<string, string> = {};
		if (this.micToken) {
			authPayload.micToken = this.micToken;
		} else if (this.consultationLink) {
			authPayload.consultationLink = this.consultationLink;
			if (this.admissionToken) authPayload.admissionToken = this.admissionToken;
		}
		return authPayload;
	}

	private setupEventHandlers() {
		if (!this.socket) return;

		this.socket.on('connect', () => {
			console.log('SocketService: Connected with ID:', this.socket?.id);

			this.reconnectAttempts = 0;
			if (this.reconnectTimer) {
				clearTimeout(this.reconnectTimer);
				this.reconnectTimer = null;
			}

			this.joinRoom();
			this.sendPendingAudioData();
		});

		this.socket.on('disconnect', (reason: string, details?: unknown) => {
			const code = closeCodeOf(details);
			console.log('SocketService: Disconnected:', reason, code ?? '');

			if (reason === 'io server disconnect') {
				this.attemptReconnect();
				return;
			}
			// A drain (1012) is not about the session: Socket.IO's own randomised backoff reconnects.
			if ((reason === 'transport close' || reason === 'transport error' || reason === 'ping timeout') && code !== CLOSE_SERVICE_RESTART && this.reliesOnSession()) {
				this.recoverSession(code, true);
			}
		});

		this.socket.on('connect_error', (error: any) => {
			console.error('SocketService: Connection error:', error?.message ?? error);
			if (this.reliesOnSession()) {
				this.recoverSession(undefined, false);
				return;
			}
			this.attemptReconnect();
		});

		this.socket.on('error', (error: any) => {
			console.error('SocketService: Socket error:', error);
		});
	}

	// Only a practitioner's socket is admitted by the session alone; a mic token or a consultation link admits the others.
	private reliesOnSession(): boolean {
		return !this.micToken && !this.consultationLink;
	}

	// Without a live session talk would see an anonymous caller, so the session is checked before Socket.IO reconnects (contract §19).
	// After a refused handshake the retry still backs off: talk may be refusing for a reason the session cannot fix.
	private recoverSession(closeCode: number | undefined, reconnectNow: boolean) {
		const socket = this.socket;
		if (!socket || this.recoveringSession) return;
		this.recoveringSession = true;
		// Synchronous on purpose: the Manager decides to reconnect right after this event returns.
		socket.io?.reconnection?.(false);
		const check = closeCode === CLOSE_REAUTHENTICATE ? reauthenticateAfterClose() : confirmSessionBeforeReconnect();
		void check.then((outcome) => {
			this.recoveringSession = false;
			if (this.socket !== socket) return;
			if (outcome === 'signed_out') {
				console.warn('SocketService: Session ended; not reconnecting');
				this.disconnect();
				return;
			}
			socket.io?.reconnection?.(true);
			if (outcome === 'reconnect' && reconnectNow) {
				socket.connect();
			} else {
				this.attemptReconnect();
			}
		});
	}

	public joinRoom() {
		if (!this.socket?.connected) {
			console.warn('SocketService: Cannot join room, socket not connected');
			return;
		}

		// A mic socket is not a participant: it streams audio and reads nothing. talk refuses both of
		// these events for it — `joinRoom` would hand a phone the room's live transcript and start the
		// broadcasts flowing to it — and pins the room type at the handshake instead. Emitting them
		// anyway would only earn an authError on every connect and reconnect.
		if (this.micToken) {
			console.log('SocketService: Mic socket — streaming only, not joining the room');
			return;
		}

		console.log('SocketService: Joining room');
		this.socket.emit('joinRoom', this.roomId);
		this.socket.emit('setRoomType', { roomId: this.roomId, roomType: this.roomType });
	}

	private attemptReconnect() {
		if (this.reconnectTimer) return;

		if (this.reconnectAttempts < this.maxReconnectAttempts) {
			this.reconnectAttempts++;

			const delay = Math.min(
				this.baseReconnectDelay * Math.pow(1.5, this.reconnectAttempts) * (0.8 + Math.random() * 0.4),
				30000
			);

			console.log(`SocketService: Attempting reconnect in ${Math.round(delay / 1000)}s (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

			this.reconnectTimer = setTimeout(() => {
				// REOPEN the existing socket — never discard it and build a new one.
				//
				// This used to `close()` the socket, null it, and call `connect()`, which does
				// `this.socket = io(...)`: a brand-new Socket object. `setupEventHandlers()` then
				// re-registered THIS SERVICE's four handlers and nothing else — every listener the
				// consult pages had attached with `socket.on(...)` (`offerRequested`, `videoSignal`,
				// `participantJoined`, `admittedToConsultation`, and each page's own `connect`
				// handler) was bound to the discarded object and silently lost.
				//
				// The symptom was a call that could never recover: the new socket connected and
				// logged a fresh id, `joinRoom()` fired from here, but the page's `connect` handler
				// never ran so `joinVideoRoom` was never re-emitted, and with no `offerRequested` or
				// `videoSignal` listener the server's offer fan-out reached nothing. Both parties had
				// to reload the page, because only a remount re-registers those handlers.
				//
				// `connect()` on the same instance reopens the transport and keeps every listener, so
				// the page's own reconnect path (which already re-emits `joinVideoRoom`) runs.
				if (this.socket) {
					this.socket.connect();
				} else {
					this.connect();
				}
				this.reconnectTimer = null;
			}, delay);
		} else {
			console.error('SocketService: Maximum reconnection attempts reached');
		}
	}

	public async sendAudio(audioData: {
		roomId: string;
		participantId: string;
		roomType?: string; // NEW
		timestamp: number;
		// Wall clock of the first sample in the packet (`timestamp` is stamped when it finished).
		// transcribe's contract-v2 anchors absolute segment times on it; talk passes it through.
		audioStartTimestamp?: number;
		// Capture rate of the WAV — 16kHz where the browser honoured the hint, the device rate
		// otherwise. Forwarded so talk does not have to parse the header.
		sampleRate?: number;
		audioId?: string;
		data: ArrayBuffer
	}): Promise<boolean> {
		const audioId = audioData.audioId || `audio-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

		if (audioData.audioId && this.sentAudioIds.has(audioData.audioId)) {
			console.warn(`SocketService: Skipping duplicate audio segment by ID: ${audioData.audioId}`);
			return true;
		}

		this.sentAudioIds.add(audioId);

		if (this.sentAudioIds.size > 1000) {
			const oldestIds = Array.from(this.sentAudioIds).slice(0, 500);
			oldestIds.forEach(id => this.sentAudioIds.delete(id));
		}

		if (this.ongoingTransmissions.has(audioId)) {
			console.warn(`SocketService: Already sending audio ${audioId}, adding to pending queue`);
			this.queuePending({ ...audioData, audioId });
			return false;
		}

		if (!this.socket?.connected) {
			console.warn('SocketService: Socket not connected, storing audio for later transmission');
			this.queuePending({ ...audioData, audioId });
			return false;
		}

		try {
			this.ongoingTransmissions.add(audioId);

			const MAX_CHUNK_SIZE = 64 * 1024;
			const audioBuffer = audioData.data;

			if (audioBuffer.byteLength <= MAX_CHUNK_SIZE) {
				this.socket.emit('final-audio', { ...audioData, audioId });
				console.log(`SocketService: Sent audio segment ${audioId} (${audioBuffer.byteLength} bytes, type: ${audioData.roomType || 'unknown'})`);

				this.ongoingTransmissions.delete(audioId);
				return true;
			} else {
				const totalChunks = Math.ceil(audioBuffer.byteLength / MAX_CHUNK_SIZE);
				console.log(`SocketService: Breaking audio ${audioId} into ${totalChunks} chunks`);

				for (let i = 0; i < totalChunks; i++) {
					const start = i * MAX_CHUNK_SIZE;
					const end = Math.min(start + MAX_CHUNK_SIZE, audioBuffer.byteLength);
					const chunk = audioBuffer.slice(start, end);

					this.socket.emit('audio-chunk', {
						roomId: audioData.roomId,
						participantId: audioData.participantId,
						roomType: audioData.roomType, // NEW
						timestamp: audioData.timestamp,
						// Every real chunk lands in this branch, so a field enumerated only on the
						// single-emit path above would ship inert.
						audioStartTimestamp: audioData.audioStartTimestamp,
						sampleRate: audioData.sampleRate,
						audioId: audioId,
						chunkIndex: i,
						totalChunks: totalChunks,
						isLastChunk: i === totalChunks - 1,
						data: chunk
					});

					if (i < totalChunks - 1) {
						await new Promise(resolve => setTimeout(resolve, 20));
					}
				}

				this.ongoingTransmissions.delete(audioId);
				return true;
			}
		} catch (error) {
			console.error('SocketService: Error sending audio data:', error);
			this.ongoingTransmissions.delete(audioId);

			this.queuePending({ ...audioData, audioId });
			return false;
		}
	}

	/**
	 * The single way into `pendingAudioData` — drop the oldest segment once the queue is at the cap.
	 * `MAX_PENDING_AUDIO` used to be enforced on the send-threw path alone, which is the one path a
	 * plain disconnect never takes: the queue was unbounded on exactly the path that fills it, and a
	 * long drop grew it a WAV buffer at a time until the tab died and the whole consult went with it.
	 */
	private queuePending(audioData: PendingAudio) {
		if (this.pendingAudioData.length >= MAX_PENDING_AUDIO) {
			this.pendingAudioData.shift();
			console.warn('SocketService: Pending audio queue full — dropping oldest segment');
		}
		this.pendingAudioData.push(audioData);
	}

	private async sendPendingAudioData() {
		if (!this.socket?.connected || this.pendingAudioData.length === 0) return;

		console.log(`SocketService: Sending ${this.pendingAudioData.length} pending audio segments`);

		const dataCopy = [...this.pendingAudioData];
		this.pendingAudioData = [];

		for (const audioData of dataCopy) {
			// The failed first attempt already registered this segment against the dedup guard —
			// `sendAudio` adds the id BEFORE it checks the socket — so handing it straight back would
			// hit `Skipping duplicate audio segment by ID`, return a false `true` without emitting,
			// and discard the segment for good: the queue is already cleared above. Clear the id so
			// the replay is treated as the first real send it is.
			if (audioData.audioId) this.sentAudioIds.delete(audioData.audioId);

			try {
				await this.sendAudio(audioData);
				await new Promise(resolve => setTimeout(resolve, 50));
			} catch (error) {
				console.error('SocketService: Error sending pending audio:', error);
				this.queuePending(audioData);
			}
		}
	}

	public disconnect() {
		if (this.socket) {
			this.socket.disconnect();
			this.socket = null;
		}

		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}

		this.reconnectAttempts = 0;
		this.recoveringSession = false;
		this.isInitialized = false;
		this.admissionToken = null;
		this.sentAudioIds.clear();
		this.ongoingTransmissions.clear();
		console.log('SocketService: Disconnected and cleaned up');
	}
}

export default SocketService;